/**
 * /approvals — organisation-wide approval queue.
 *
 * Phase 2 additions:
 *   - Three tabs: "Awaiting my action" / "I requested" / "Recently completed"
 *   - Analytics panel (collapsible) showing counts + latency
 *   - AI Policy Recommendations card (admin only)
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  analyticsQueryOptions,
  approvalControlsSummaryQueryOptions,
  approvalPoliciesQueryOptions,
  approvalRecommendationsQueryOptions,
  approvalRequestsListQueryOptions,
} from "@/lib/queries";
import { api } from "@/lib/api";
import { meQueryOptions } from "@/lib/queries";
import { HelpHint } from "@/components/HelpHint";
import { ApprovalAISummaryPanel } from "@/components/ApprovalAISummaryPanel";
import type {
  AIConfidence,
  ApprovalRecommendation,
  ApprovalRequest,
  ApprovalState,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/approvals/")({
  component: ApprovalsPage,
});

// ──────────────────────────────────────────────────────────
// Presentational helpers
// ──────────────────────────────────────────────────────────
const STATE_COLOR: Record<ApprovalState, "default" | "warning" | "success" | "error"> = {
  pending: "warning",
  approved: "success",
  rejected: "error",
  cancelled: "default",
};

const CONFIDENCE_DOT: Record<AIConfidence, string> = {
  high: "bg-emerald-500",
  medium: "bg-amber-400",
  low: "bg-red-400",
};

const AI_REC_COLOR: Record<string, "success" | "error" | "warning" | "default"> = {
  approve: "success",
  reject: "error",
  escalate: "warning",
  hold: "default",
};

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function fmtMoney(amount: string | null, ccy: string | null) {
  if (!amount || !ccy) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: ccy,
    maximumFractionDigits: 2,
  }).format(Number(amount));
}

function fmtHours(h: number | null | undefined) {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  return `${h.toFixed(1)}h`;
}

const SOURCE_LABEL: Record<string, string> = {
  purchase_order: "PO",
  ap_invoice: "AP Invoice",
  vendor_bill: "Vendor bill",
  ar_invoice: "AR Invoice",
  manual: "Manual",
};

// ──────────────────────────────────────────────────────────
// Approvals table
// ──────────────────────────────────────────────────────────
function ApprovalsTable({ rows }: { rows: ApprovalRequest[] }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<ApprovalRequest | null>(null);

  const handleRowClick = (row: ApprovalRequest) => {
    setSelected(row);
  };

  if (rows.length === 0) {
    return (
      <div className="text-center text-slate-400 text-sm py-16 dark:text-slate-500">
        <AccountTreeIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
        <p>No approval requests in this view.</p>
      </div>
    );
  }

  return (
    <>
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm dark:bg-gray-900 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200 dark:bg-gray-950 dark:border-gray-800">
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">Source</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">Amount</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">Tier</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">AI Rec</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">Recommended approver</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">State</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">Controls</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">Requested</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => {
            const rec = row.ai_recommendation_json;
            const tierRec = rec?.routing.find((r) => r.tier === row.current_tier_index);
            const recommended = tierRec?.recommended;
            const aiAction = rec ? "routed" : undefined;
            const controlStatus = row.control_evaluation_json?.status;

            return (
              <tr
                key={row.id}
                onClick={() => handleRowClick(row)}
                className="cursor-pointer transition-colors hover:bg-blue-50 dark:hover:bg-blue-950/20"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Chip
                      label={SOURCE_LABEL[row.source_type] ?? row.source_type}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 11, height: 20 }}
                    />
                    <span className="font-mono text-xs text-slate-600 dark:text-slate-300">
                      {row.source_id.slice(0, 8)}
                    </span>
                    <span className="text-[10px] text-blue-500">Review →</span>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-sm text-slate-700 dark:text-slate-300">
                  {fmtMoney(row.amount, row.currency)}
                </td>
                <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                  Tier {row.current_tier_index + 1}
                </td>
                <td className="px-4 py-3">
                  {aiAction ? (
                    <Chip
                      label={aiAction}
                      size="small"
                      color={AI_REC_COLOR[aiAction] ?? "default"}
                      variant="filled"
                      sx={{ fontSize: 11, height: 20 }}
                    />
                  ) : (
                    <span className="text-xs text-slate-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {recommended ? (
                    <div className="flex items-center gap-1.5">
                      <PersonOutlineIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                      <span className="text-xs text-slate-700 dark:text-slate-300">{recommended.approver_name}</span>
                      <Tooltip title={`Confidence: ${recommended.confidence}. ${recommended.rationale}`}>
                        <span
                          className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                            CONFIDENCE_DOT[recommended.confidence as AIConfidence] ?? "bg-slate-300"
                          }`}
                        />
                      </Tooltip>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Chip
                    label={row.state.replace("_", " ")}
                    size="small"
                    color={STATE_COLOR[row.state]}
                    variant={row.state === "approved" ? "filled" : "outlined"}
                    icon={
                      row.state === "approved"
                        ? <CheckCircleIcon sx={{ fontSize: "12px !important" }} />
                        : row.state === "rejected"
                        ? <CancelIcon sx={{ fontSize: "12px !important" }} />
                        : undefined
                    }
                    sx={{ fontSize: 11, height: 22 }}
                  />
                </td>
                <td className="px-4 py-3">
                  <Chip
                    label={
                      row.control_evaluation_json?.legacy_compatibility
                        ? "Legacy"
                        : controlStatus === "blocked"
                          ? "Blocked"
                          : controlStatus === "complete"
                            ? "Complete"
                            : "Enforced"
                    }
                    size="small"
                    color={
                      controlStatus === "blocked"
                        ? "error"
                        : row.control_evaluation_json?.legacy_compatibility
                          ? "warning"
                          : "success"
                    }
                    variant="outlined"
                    sx={{ fontSize: 10, height: 20 }}
                  />
                </td>
                <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                  {fmtDate(row.requested_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    <Dialog
      open={Boolean(selected)}
      onClose={() => setSelected(null)}
      maxWidth="md"
      fullWidth
    >
      {selected && (
        <>
          <DialogTitle sx={{ fontSize: 16, fontWeight: 600, pb: 1 }}>
            Approval controls · {SOURCE_LABEL[selected.source_type] ?? selected.source_type}
          </DialogTitle>
          <DialogContent dividers>
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  {selected.source_id}
                </p>
                <p className="text-xs text-slate-500">
                  {fmtMoney(selected.amount, selected.currency)} · Tier {selected.current_tier_index + 1}
                </p>
              </div>
              {selected.source_type === "purchase_order" && (
                <button
                  type="button"
                  onClick={() => {
                    setSelected(null);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    navigate({ to: "/purchase-orders/$poId", params: { poId: selected.source_id } } as any);
                  }}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                >
                  Open source document
                </button>
              )}
            </div>
            <ApprovalAISummaryPanel requestId={selected.id} />
          </DialogContent>
        </>
      )}
    </Dialog>
    </>
  );
}

// ──────────────────────────────────────────────────────────
// Analytics panel
// ──────────────────────────────────────────────────────────
function AnalyticsPanel() {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    ...analyticsQueryOptions(),
    enabled: open,
  });
  const d = q.data;

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm mb-4 dark:bg-gray-900 dark:border-gray-800">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition dark:text-slate-300 dark:hover:bg-gray-800"
      >
        <span className="flex items-center gap-2">
          <AccountTreeIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
          Approval Analytics (last 90 days)
          {q.isLoading && open && <CircularProgress size={12} />}
        </span>
        {open ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
      </button>
      <Collapse in={open}>
        <div className="px-4 pb-4 border-t border-slate-100 dark:border-gray-800">
          {q.isError && (
            <p className="text-xs text-red-500 mt-3">Failed to load analytics.</p>
          )}
          {d && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
              <StatCard label="Total requests" value={d.total_requests} />
              <StatCard label="Pending" value={d.pending_count} />
              <StatCard
                label="Approval rate"
                value={`${(d.approval_rate * 100).toFixed(1)}%`}
              />
              <StatCard
                label="Avg latency"
                value={fmtHours(d.avg_approval_latency_hours)}
              />
            </div>
          )}
          {d && d.bottleneck_approvers.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 dark:text-slate-400">
                Slowest approvers
              </p>
              <ul className="text-xs text-slate-600 space-y-0.5 dark:text-slate-300">
                {d.bottleneck_approvers.map((b) => (
                  <li key={b.approver_id}>
                    <span className="font-mono">{b.approver_id.slice(0, 8)}</span>
                    {" — "}avg {fmtHours(b.avg_response_hours)} ({b.action_count} actions)
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-slate-50 rounded-md p-3 dark:bg-gray-950">
      <p className="text-xs text-slate-500 mb-0.5 dark:text-slate-400">{label}</p>
      <p className="text-lg font-semibold text-slate-800 dark:text-slate-200">{value}</p>
    </div>
  );
}

function ControlsPanel({ onReviewPending }: { onReviewPending: () => void }) {
  const q = useQuery(approvalControlsSummaryQueryOptions());
  const d = q.data;
  if (q.isLoading) {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500 dark:border-gray-800 dark:bg-gray-900">
        <CircularProgress size={12} /> Checking approval controls…
      </div>
    );
  }
  if (q.isError || !d) return null;
  const healthy = d.active_policies > 0 && d.legacy_policies === 0 && d.blocked_requests === 0 && d.evidence_integrity_failures === 0;
  return (
    <div className={`mb-4 rounded-lg border bg-white shadow-sm dark:bg-gray-900 ${healthy ? "border-emerald-200 dark:border-emerald-900" : "border-amber-200 dark:border-amber-900"}`}>
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 dark:border-gray-800">
        {healthy ? (
          <GppGoodOutlinedIcon sx={{ fontSize: 18, color: "#059669" }} />
        ) : (
          <WarningAmberIcon sx={{ fontSize: 18, color: "#d97706" }} />
        )}
        <div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Audit & controls</p>
          <p className="text-[11px] text-slate-500">
            {healthy ? "Approval governance is operating cleanly." : d.active_policies === 0 ? "No active approval matrix is protecting new transactions." : "One or more control items need controller review."}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-px bg-slate-100 md:grid-cols-5 dark:bg-gray-800">
        <ControlMetric label="Controlled policies" value={`${d.controlled_policies}/${d.active_policies}`} tone={d.legacy_policies ? "warning" : "good"} />
        <ControlMetric label="Legacy policies" value={d.legacy_policies} tone={d.legacy_policies ? "warning" : "good"} />
        <ControlMetric label="Blocked requests" value={d.blocked_requests} tone={d.blocked_requests ? "bad" : "good"} />
        <ControlMetric label="AI evidence" value={d.ai_evidence_records} tone="neutral" />
        <ControlMetric label="Integrity failures" value={d.evidence_integrity_failures} tone={d.evidence_integrity_failures ? "bad" : "good"} />
      </div>
      {d.control_overrides_90d > 0 && (
        <p className="px-4 py-2 text-[11px] text-slate-500">
          {d.control_overrides_90d} routing or escalation override{d.control_overrides_90d === 1 ? "" : "s"} recorded in the last 90 days.
        </p>
      )}
      {d.pending_requests > 0 && (
        <button
          type="button"
          onClick={onReviewPending}
          className="mx-4 mb-3 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-slate-200 dark:hover:bg-gray-800"
        >
          Review all {d.pending_requests} pending request{d.pending_requests === 1 ? "" : "s"}
        </button>
      )}
    </div>
  );
}

function ControlMetric({ label, value, tone }: { label: string; value: number | string; tone: "good" | "warning" | "bad" | "neutral" }) {
  const color = tone === "bad" ? "text-red-700" : tone === "warning" ? "text-amber-700" : tone === "good" ? "text-emerald-700" : "text-slate-800";
  return (
    <div className="bg-white px-4 py-3 dark:bg-gray-900">
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-0.5 text-lg font-semibold ${color}`}>{value}</p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Recommendations card (admin only)
// ──────────────────────────────────────────────────────────
function RecommendationsCard() {
  const qc = useQueryClient();
  const recsQ = useQuery(approvalRecommendationsQueryOptions());
  const policiesQ = useQuery(approvalPoliciesQueryOptions());

  const activePolicyId = policiesQ.data?.find((p) => p.is_active)?.id ?? null;

  const generateMutation = useMutation({
    mutationFn: () =>
      api<ApprovalRecommendation[]>(
        "/api/money/approvals/policy/recommendations/generate",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ policy_id: activePolicyId }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...["approvals"], "recommendations"] }),
  });

  const acceptMutation = useMutation({
    mutationFn: (recId: string) =>
      api(`/api/money/approvals/policy/recommendations/${recId}/accept`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approvals"] }),
  });

  const dismissMutation = useMutation({
    mutationFn: (recId: string) =>
      api(`/api/money/approvals/policy/recommendations/${recId}/dismiss`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...["approvals"], "recommendations"] }),
  });

  const recs = recsQ.data ?? [];

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm mb-4 dark:bg-gray-900 dark:border-gray-800">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-gray-800">
        <span className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
          <AutoFixHighIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
          AI Policy Recommendations
        </span>
        <button
          type="button"
          disabled={!activePolicyId || generateMutation.isPending}
          onClick={() => generateMutation.mutate()}
          className="text-xs px-3 py-1 rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition"
        >
          {generateMutation.isPending ? "Generating…" : "Generate recommendations"}
        </button>
      </div>

      {recsQ.isLoading && (
        <div className="px-4 py-3 text-xs text-slate-400 flex gap-2 items-center dark:text-slate-500">
          <CircularProgress size={12} /> Loading…
        </div>
      )}

      {!recsQ.isLoading && recs.length === 0 && (
        <div className="px-4 py-4 text-xs text-slate-400 text-center dark:text-slate-500">
          No pending recommendations. Click "Generate recommendations" to analyse the active policy.
        </div>
      )}

      {recs.map((rec) => (
        <div key={rec.id} className="px-4 py-3 border-b border-slate-50 last:border-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <Chip
                label={rec.recommendation_type.replace(/_/g, " ")}
                size="small"
                variant="outlined"
                sx={{ fontSize: 10, height: 18, mb: 0.5 }}
              />
              <p className="text-xs text-slate-700 mt-1 dark:text-slate-300">{rec.rationale}</p>
              <p className="text-[10px] text-slate-400 mt-0.5 dark:text-slate-500">
                Generated {new Date(rec.generated_at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                disabled={acceptMutation.isPending || !rec.suggested_tiers_json}
                onClick={() => acceptMutation.mutate(rec.id)}
                className="text-xs px-2.5 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition"
              >
                Accept
              </button>
              <button
                type="button"
                disabled={dismissMutation.isPending}
                onClick={() => dismissMutation.mutate(rec.id)}
                className="text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition dark:border-gray-700 dark:text-slate-300 dark:hover:bg-gray-800"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────
function ApprovalsPage() {
  const [tab, setTab] = useState(0);
  const meQ = useQuery(meQueryOptions());
  const authUser = meQ.data;
  const isAdmin = authUser?.role === "admin";

  // Tab 0: pending awaiting my action
  const pendingQ = useQuery(approvalRequestsListQueryOptions({ state: "pending" }));
  // Tab 1/2 are the current user's submitted requests; requester separation
  // means they usually will not also be the assigned approver.
  const allQ = useQuery(approvalRequestsListQueryOptions({ requested_by_me: true }));
  const completedQ = useQuery(approvalRequestsListQueryOptions({ state: "approved", requested_by_me: true }));
  // Controller/admin organisation-wide pending queue.
  const allPendingQ = useQuery({
    ...approvalRequestsListQueryOptions({ state: "pending", mine_only: false }),
    enabled: isAdmin,
  });

  const pendingRows: ApprovalRequest[] = pendingQ.data ?? [];
  // Filter tab 1 client-side: requested by current user
  const myRows: ApprovalRequest[] = (allQ.data ?? []).filter(
    (r) => r.requested_by === authUser?.id,
  );
  const completedRows: ApprovalRequest[] = completedQ.data ?? [];

  const activeQ =
    tab === 0 ? pendingQ
    : tab === 1 ? allQ
    : tab === 2 ? completedQ
    : allPendingQ;

  const rows =
    tab === 0 ? pendingRows
    : tab === 1 ? myRows
    : tab === 2 ? completedRows
    : (allPendingQ.data ?? []);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] bg-slate-50 dark:bg-gray-950">
      {/* Page header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <AccountTreeIcon sx={{ fontSize: 22, color: "#3b82f6" }} />
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Approvals</h1>
          {activeQ.isLoading && <CircularProgress size={14} />}
          <div className="ml-auto flex items-center gap-2">
            <HelpHint docPath="approvals/audit-controls.md" label="Audit controls help" />
            <HelpHint docPath="approvals/overview.md" label="Approvals help" />
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
          AI-routed approval requests across all sources.
        </p>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v as number)}
          sx={{ mt: 1, minHeight: 36 }}
          TabIndicatorProps={{ style: { height: 2 } }}
        >
          <Tab
            label={`Awaiting my action${pendingRows.length ? ` (${pendingRows.length})` : ""}`}
            sx={{ fontSize: 12, minHeight: 36, textTransform: "none", py: 0 }}
          />
          <Tab
            label={`I requested${myRows.length ? ` (${myRows.length})` : ""}`}
            sx={{ fontSize: 12, minHeight: 36, textTransform: "none", py: 0 }}
          />
          <Tab
            label="Recently completed"
            sx={{ fontSize: 12, minHeight: 36, textTransform: "none", py: 0 }}
          />
          {isAdmin && (
            <Tab
              label={`All pending${allPendingQ.data?.length ? ` (${allPendingQ.data.length})` : ""}`}
              sx={{ fontSize: 12, minHeight: 36, textTransform: "none", py: 0 }}
            />
          )}
        </Tabs>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {/* Analytics panel (collapsible) */}
        {isAdmin && <ControlsPanel onReviewPending={() => setTab(3)} />}

        {/* Analytics panel (collapsible) */}
        <AnalyticsPanel />

        {/* Recommendations (admin only) */}
        {isAdmin && <RecommendationsCard />}

        {/* Error state */}
        {activeQ.isError && (
          <div className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3 border border-red-200 mb-4">
            Failed to load approvals. {(activeQ.error as Error)?.message}
          </div>
        )}

        {/* Table */}
        <ApprovalsTable rows={rows} />
      </div>
    </div>
  );
}
